import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { stockApi } from '../lib/api';
import { 
  ArrowLeft, 
  TrendingUp, 
  DollarSign, 
  Activity, 
  Shield,
  Building2
} from 'lucide-react';
import { StockLogo } from '../components/StockLogo';
import type { MetricBreakdown, Tag, NewsArticle } from '../types/api';
import { useState, useEffect } from 'react';

export function StockDetailPage() {
  const { symbol } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const [hoveredPoint, setHoveredPoint] = useState<{ date: string; price: number; x: number; y: number; svgX: number; svgY: number } | null>(null);
  const [expandedNews, setExpandedNews] = useState<Set<string>>(new Set());

  const { data: stockData, isLoading, error } = useQuery({
    queryKey: ['stockDetail', symbol],
    queryFn: () => stockApi.getStockDetail(symbol!),
    enabled: !!symbol,
    staleTime: 0, // Always consider data stale, refetch on mount
    refetchOnMount: true, // Always refetch when component mounts
  });

  // Debug: Log date range when data loads (must be before any conditional returns)
  useEffect(() => {
    if (stockData?.daily_prices && stockData.daily_prices.length > 0) {
      const sortedPrices = [...stockData.daily_prices].sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const earliestDate = sortedPrices[0]?.date;
      const latestDate = sortedPrices[sortedPrices.length - 1]?.date;
      const today = new Date().toISOString().split('T')[0];
      
      const allDates = sortedPrices.map(p => p.date);
      console.log('📊 Stock Price Data Analysis:', {
        symbol,
        totalRecords: stockData.daily_prices.length,
        earliestDate,
        latestDate,
        today,
        daysSinceLatest: latestDate ? Math.floor((new Date(today).getTime() - new Date(latestDate).getTime()) / (1000 * 60 * 60 * 24)) : 'N/A',
        first5Dates: allDates.slice(0, 5),
        last5Dates: allDates.slice(-5)
      });
    }
  }, [stockData, symbol]);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Good':
        return '#3ECF8E';
      case 'Average':
        return '#FBBF24';
      case 'Poor':
        return '#EF4444';
      default:
        return '#9E9E9E';
    }
  };


  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F0F0F' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#3ECF8E', borderTopColor: 'transparent' }}></div>
          <p style={{ color: '#9E9E9E' }}>Loading stock details...</p>
        </div>
      </div>
    );
  }

  if (error || !stockData) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F0F0F' }}>
        <div className="text-center">
          <p className="text-xl mb-4" style={{ color: '#EF4444' }}>Failed to load stock details</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 rounded-lg"
              style={{ backgroundColor: '#3ECF8E', color: '#0F0F0F' }}
            >
              Back to Dashboard
            </button>
        </div>
      </div>
    );
  }

  const { stock, scores, tags, news, sentiment, daily_prices, latest_score_calculation } = stockData;

  return (
    <div className="min-h-screen" style={{ 
      background: 'radial-gradient(ellipse at top, #1a1a1a 0%, #0F0F0F 50%, #0a0a0a 100%)',
      position: 'relative'
    }}>
      {/* Noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.015]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      <div className="relative z-10">
        {/* Header */}
        <header className="border-b backdrop-blur-sm sticky top-0 z-40" style={{ borderColor: 'rgba(255, 255, 255, 0.1)', backgroundColor: 'rgba(15, 15, 15, 0.8)' }}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 mb-3 sm:mb-4 transition-colors min-h-[44px]"
              style={{ color: '#9E9E9E' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#F9F9F9'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9E9E9E'}
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>

            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <StockLogo symbol={stock.symbol} size={80} className="flex-shrink-0 mx-auto sm:mx-0" />
              <div className="flex-1 w-full min-w-0">
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 break-words" style={{ color: '#F9F9F9' }}>
                  {stock.name}
                </h1>
                <p className="text-lg sm:text-xl mb-3" style={{ color: '#9E9E9E' }}>
                  {stock.symbol}
                </p>
                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm" style={{ color: '#9E9E9E' }}>
                  <span className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <span className="break-words">{stock.industry}</span>
                  </span>
                  <span className="hidden sm:inline">•</span>
                  <span className="break-words">{stock.sector}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-xl sm:text-2xl font-bold whitespace-nowrap" style={{ color: '#F9F9F9' }}>
                    ${stock.current_price.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto px-4 py-6 sm:py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Left Column - Scores */}
            <div className="lg:col-span-2 space-y-8">
              {/* Overview Section */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#F9F9F9' }}>Overview</h2>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                </div>
              </div>

              {/* Price Chart */}
              {daily_prices && daily_prices.length > 0 && (
                <div className="rounded-xl p-4 sm:p-6 md:p-8 border" style={{
                  backgroundColor: 'rgba(28, 28, 32, 0.7)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  
                  {(() => {
                    // Sort by date to ensure proper ordering (oldest to newest)
                    const prices = [...daily_prices].sort((a, b) => 
                      new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    const closePrices = prices.map(p => p.close);
                    const minPrice = Math.min(...closePrices);
                    const maxPrice = Math.max(...closePrices);
                    const priceRange = maxPrice - minPrice;
                    const padding = priceRange * 0.1; // 10% padding
                    
                    const chartHeight = 150;
                    // Use a standard width for viewBox, SVG will scale to container
                    const chartWidth = 800;
                    
                    // Calculate points for the line
                    const points = prices.map((price, index) => {
                      const x = (index / (prices.length - 1)) * chartWidth;
                      const normalizedPrice = (price.close - minPrice + padding) / (priceRange + 2 * padding);
                      const y = chartHeight - (normalizedPrice * chartHeight);
                      return `${x},${y}`;
                    }).join(' ');
                    
                    const firstPrice = prices[0].close;
                    const lastPrice = prices[prices.length - 1].close;
                    const priceChange = lastPrice - firstPrice;
                    const priceChangePct = (priceChange / firstPrice) * 100;
                    
                    return (
                      <div>
                        {/* Price Summary */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
                          <div>
                            <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Current Price</div>
                            <div className="text-xl sm:text-2xl font-bold" style={{ color: '#F9F9F9' }}>
                              ${lastPrice.toFixed(2)}
                            </div>
                          </div>
                          <div className="text-left sm:text-right">
                            <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>30-Day Change</div>
                            <div 
                              className="text-lg sm:text-xl font-bold"
                              style={{ color: priceChange >= 0 ? '#3ECF8E' : '#EF4444' }}
                            >
                              {priceChangePct >= 0 ? '+' : ''}{priceChangePct.toFixed(2)}%
                            </div>
                          </div>
                        </div>
                        
                        {/* Chart */}
                        <div className="relative w-full overflow-hidden" style={{ height: chartHeight + 40 }}>
                          <svg 
                            viewBox={`0 0 ${chartWidth} ${chartHeight}`} 
                            className="w-full h-full"
                            preserveAspectRatio="xMidYMid meet"
                            onMouseMove={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              const svgX = ((e.clientX - rect.left) / rect.width) * chartWidth;
                              const closestIndex = Math.round((svgX / chartWidth) * (prices.length - 1));
                              
                              if (closestIndex >= 0 && closestIndex < prices.length) {
                                const price = prices[closestIndex];
                                const pointX = (closestIndex / (prices.length - 1)) * chartWidth;
                                const normalizedPrice = (price.close - minPrice + padding) / (priceRange + 2 * padding);
                                const pointY = chartHeight - (normalizedPrice * chartHeight);
                                
                                // Calculate percentage for tooltip positioning
                                const xPercent = (e.clientX - rect.left) / rect.width * 100;
                                const yPercent = (e.clientY - rect.top) / rect.height * 100;
                                
                                setHoveredPoint({
                                  date: price.date,
                                  price: price.close,
                                  x: xPercent,
                                  y: yPercent,
                                  svgX: pointX,
                                  svgY: pointY
                                });
                              }
                            }}
                            onMouseLeave={() => setHoveredPoint(null)}
                          >
                            {/* Grid lines */}
                            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
                              <line
                                key={ratio}
                                x1="0"
                                y1={chartHeight * ratio}
                                x2={chartWidth}
                                y2={chartHeight * ratio}
                                stroke="rgba(255, 255, 255, 0.05)"
                                strokeWidth="1"
                              />
                            ))}
                            
                            {/* Price line */}
                            <polyline
                              points={points}
                              fill="none"
                              stroke="#3ECF8E"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            
                            {/* Gradient fill */}
                            <defs>
                              <linearGradient id="priceGradient" x1="0" x2="0" y1="0" y2="1">
                                <stop offset="0%" stopColor="#3ECF8E" stopOpacity="0.2" />
                                <stop offset="100%" stopColor="#3ECF8E" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <polygon
                              points={`0,${chartHeight} ${points} ${chartWidth},${chartHeight}`}
                              fill="url(#priceGradient)"
                            />
                            
                            {/* Hover point */}
                            {hoveredPoint && (
                              <circle
                                cx={hoveredPoint.svgX}
                                cy={hoveredPoint.svgY}
                                r="4"
                                fill="#3ECF8E"
                                stroke="#0F0F0F"
                                strokeWidth="2"
                              />
                            )}
                          </svg>
                          
                          {/* Tooltip */}
                          {hoveredPoint && (
                            <div
                              className="absolute px-3 py-2 rounded-lg border pointer-events-none z-10"
                              style={{
                                left: `clamp(8px, ${hoveredPoint.x}%, calc(100% - 120px))`,
                                top: `${Math.max(8, hoveredPoint.y - 15)}%`,
                                transform: 'translateY(-100%)',
                                backgroundColor: 'rgba(26, 26, 26, 0.95)',
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                              }}
                            >
                              <div className="text-xs font-semibold mb-1" style={{ color: '#3ECF8E' }}>
                                ${hoveredPoint.price.toFixed(2)}
                              </div>
                              <div className="text-xs" style={{ color: '#9E9E9E' }}>
                                {new Date(hoveredPoint.date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Date labels - 5 intervals */}
                          <div className="flex justify-between mt-2 text-xs gap-1" style={{ color: '#9E9E9E' }}>
                            {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                              const index = Math.floor(ratio * (prices.length - 1));
                              const price = prices[index];
                              if (!price) return null;
                              
                              return (
                                <span key={idx} className="truncate">
                                  {new Date(price.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Scores Section */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#F9F9F9' }}>Scores</h2>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                </div>
              </div>

              {/* Four Scores */}
              <div className="rounded-xl p-4 sm:p-6 md:p-8 border" style={{
                  backgroundColor: 'rgba(32, 28, 28, 0.7)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {/* Value */}
                  <div className="p-4 sm:p-5 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <DollarSign className="w-5 h-5" style={{ color: '#3ECF8E' }} />
                      <span className="text-sm" style={{ color: '#9E9E9E' }}>Value</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-semibold" style={{ color: getTierColor(scores.value.tier) }}>
                      {scores.value.tier}
                    </span>
                  </div>

                  {/* Growth */}
                  <div className="p-4 sm:p-5 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <TrendingUp className="w-5 h-5" style={{ color: '#3ECF8E' }} />
                      <span className="text-sm" style={{ color: '#9E9E9E' }}>Growth</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-semibold" style={{ color: getTierColor(scores.growth.tier) }}>
                      {scores.growth.tier}
                    </span>
                  </div>

                  {/* Health */}
                  <div className="p-4 sm:p-5 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Activity className="w-5 h-5" style={{ color: '#3ECF8E' }} />
                      <span className="text-sm" style={{ color: '#9E9E9E' }}>Health</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-semibold" style={{ color: getTierColor(scores.health.tier) }}>
                      {scores.health.tier}
                    </span>
                  </div>

                  {/* Momentum */}
                  <div className="p-4 sm:p-5 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                    <div className="flex items-center gap-2 mb-3 sm:mb-4">
                      <Shield className="w-5 h-5" style={{ color: '#3ECF8E' }} />
                      <span className="text-sm" style={{ color: '#9E9E9E' }}>Momentum</span>
                    </div>
                    <span className="text-xl sm:text-2xl font-semibold" style={{ color: getTierColor(scores.momentum.tier) }}>
                      {scores.momentum.tier}
                    </span>
                  </div>
                </div>
              </div>

              {/* Key Metrics Section */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#F9F9F9' }}>Key Metrics</h2>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                </div>
              </div>

              {/* Key Metrics with Tier Colors */}
              <div className="rounded-xl p-4 sm:p-6 md:p-8 border" style={{
                  backgroundColor: 'rgba(28, 32, 28, 0.7)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {latest_score_calculation?.breakdown_json && 
                    Object.entries(latest_score_calculation.breakdown_json).map(([_, breakdowns]) => (
                      (breakdowns as MetricBreakdown[]).map((metric) => (
                        <div 
                          key={metric.metric_name} 
                          className="p-3 sm:p-4 rounded-lg border"
                          style={{ 
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            borderColor: `${getTierColor(metric.tier)}40`
                          }}
                        >
                          <div className="text-xs mb-2 break-words" style={{ color: '#9E9E9E' }}>
                            {metric.metric_name}
                          </div>
                          <div className="text-base sm:text-lg font-semibold" style={{ color: getTierColor(metric.tier) }}>
                            {metric.metric_value !== null && metric.metric_value !== undefined ? metric.metric_value.toFixed(2) : 'N/A'}
                            {metric.metric_value !== null && metric.metric_value !== undefined && (metric.metric_name.includes('%') || metric.metric_name.includes('Growth') || metric.metric_name.includes('Margin') || metric.metric_name.includes('Position')) ? '%' : ''}
                          </div>
                        </div>
                      ))
                    ))
                  }
                </div>
              </div>
            </div>

            {/* Right Column - Tags & Info */}
            <div className="lg:pl-4 space-y-6 sm:space-y-8">
              {/* Tags Section */}
              {tags && tags.length > 0 && (
                <>
                  <div className="mb-6 sm:mb-8">
                    <div className="flex items-center gap-4 mb-6">
                      <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#F9F9F9' }}>Tags</h2>
                      <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                    </div>
                  </div>
                </>
              )}
              {/* Tags */}
              {tags && tags.length > 0 && (
                <div className="rounded-xl p-4 sm:p-6 md:p-8 border" style={{
                  backgroundColor: 'rgba(28, 28, 32, 0.65)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <div className="space-y-4">
                    {tags.map((tag: Tag) => {
                      const colorMap: Record<string, string> = {
                        green: '#3ECF8E',
                        blue: '#60A5FA',
                        red: '#EF4444',
                        yellow: '#FBBF24',
                        purple: '#A78BFA',
                        pink: '#F472B6',
                        orange: '#FB923C',
                        cyan: '#22D3EE',
                      };
                      const color = colorMap[tag.color?.toLowerCase()] || '#60A5FA';
                      
                      return (
                        <div key={tag.id} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                          <div className="mb-1">
                            <span className="text-sm font-medium" style={{ color }}>
                              {tag.name}
                            </span>
                          </div>
                          {tag.description && (
                            <p className="text-xs" style={{ color: '#9E9E9E' }}>
                              {tag.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Social Sentiment */}
              {sentiment && (
                <div className="rounded-xl p-4 sm:p-6 md:p-8 border" style={{
                  backgroundColor: 'rgba(28, 28, 32, 0.65)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                  borderColor: 'rgba(255, 255, 255, 0.12)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                }}>
                  <h2 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3" style={{ color: '#F9F9F9' }}>
                    Social Sentiment
                  </h2>
                  <p className="text-xs mb-4 sm:mb-6" style={{ color: '#9E9E9E' }}>
                    Reddit and social media mentions
                  </p>
                  
                  <div className="space-y-5">
                    {/* Mentions */}
                    <div>
                      <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Mentions</div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold" style={{ color: '#F9F9F9' }}>
                          {sentiment.mentions}
                        </span>
                        <span 
                          className="text-sm font-medium"
                          style={{ color: sentiment.mentions_change >= 0 ? '#3ECF8E' : '#EF4444' }}
                        >
                          {sentiment.mentions_change >= 0 ? '+' : ''}{sentiment.mentions_change} ({sentiment.mentions_change_pct.toFixed(2)}%)
                        </span>
                      </div>
                    </div>

                    {/* Upvotes */}
                    <div>
                      <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Upvotes</div>
                      <div className="text-2xl font-bold" style={{ color: '#F9F9F9' }}>
                        {sentiment.upvotes}
                      </div>
                    </div>

                    {/* Rank */}
                    <div>
                      <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Rank</div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold" style={{ color: '#F9F9F9' }}>
                          #{sentiment.rank}
                        </span>
                        <span 
                          className="text-sm font-medium"
                          style={{ color: sentiment.rank_change >= 0 ? '#3ECF8E' : '#EF4444' }}
                        >
                          {sentiment.rank_change >= 0 ? '+' : ''}{sentiment.rank_change}
                        </span>
                      </div>
                    </div>

                    {/* Last Updated */}
                    <div className="pt-5 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                      <div className="text-xs" style={{ color: '#9E9E9E' }}>
                        Updated: {new Date(sentiment.fetched_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* News Section */}
          {news && news.length > 0 && (
            <>
              <div className="mt-12 sm:mt-16 mb-6 sm:mb-8">
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold" style={{ color: '#F9F9F9' }}>News</h2>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>
                </div>
              </div>
            </>
          )}
          {news && news.length > 0 && (
            <div className="mt-8 sm:mt-12 rounded-xl p-4 sm:p-6 md:p-8 border" style={{
              backgroundColor: 'rgba(30, 30, 30, 0.7)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderColor: 'rgba(255, 255, 255, 0.12)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="space-y-4">
                {news.map((article: NewsArticle) => {
                  const getSentimentColor = (label: string | undefined | null) => {
                    if (!label) return '#9E9E9E';
                    
                    const lowerLabel = label.toLowerCase();
                    if (lowerLabel.includes('bullish')) return '#3ECF8E';
                    if (lowerLabel.includes('bearish')) return '#EF4444';
                    if (lowerLabel.includes('neutral')) return '#9E9E9E';
                    return '#9E9E9E';
                  };

                  const getRelevanceColor = (score: number) => {
                    if (score >= 0.7) return '#3ECF8E';
                    if (score >= 0.4) return '#FBBF24';
                    return '#9E9E9E';
                  };

                  return (
                    <a
                      key={article.id}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 sm:p-4 rounded-lg transition-all border min-h-[44px]"
                      style={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        borderColor: 'rgba(255, 255, 255, 0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      }}
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-2 sm:gap-3 mb-2">
                        <h3 className="text-sm font-medium flex-1 break-words" style={{ color: '#F9F9F9' }}>
                          {article.title}
                        </h3>
                        <div className="flex gap-2 flex-wrap">
                          {article.ticker_sentiment_label && (
                            <span 
                              className="text-xs font-semibold px-2 py-1 rounded whitespace-nowrap"
                              style={{ 
                                backgroundColor: `${getSentimentColor(article.ticker_sentiment_label)}26`,
                                color: getSentimentColor(article.ticker_sentiment_label)
                              }}
                            >
                              {article.ticker_sentiment_label}
                            </span>
                          )}
                          {article.relevance_score !== undefined && (
                            <span 
                              className="text-xs font-semibold px-2 py-1 rounded whitespace-nowrap"
                              style={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: getRelevanceColor(article.relevance_score)
                              }}
                            >
                              {(article.relevance_score * 100).toFixed(0)}% relevant
                            </span>
                          )}
                        </div>
                      </div>
                      {article.summary && (
                        <div className="mb-2">
                          <p 
                            className="text-xs" 
                            style={{ 
                              color: '#9E9E9E',
                              lineHeight: '1.5',
                              ...(expandedNews.has(article.id) ? {} : {
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              })
                            }}
                          >
                            {article.summary}
                          </p>
                          {!expandedNews.has(article.id) && article.summary.length > 150 && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExpandedNews(prev => new Set([...prev, article.id]));
                              }}
                              className="text-xs mt-1 font-medium transition-colors"
                              style={{ color: '#9E9E9E' }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#F9F9F9'}
                              onMouseLeave={(e) => e.currentTarget.style.color = '#9E9E9E'}
                            >
                              Read more
                            </button>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs" style={{ color: '#9E9E9E' }}>
                        <span className="font-medium">{article.source}</span>
                        <span>•</span>
                        <span>
                          {new Date(article.time_published).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </span>
                        {article.ticker_sentiment_score !== undefined && article.ticker_sentiment_score !== null && (
                          <>
                            <span>•</span>
                            <span 
                              className="font-medium"
                              style={{ color: getSentimentColor(article.ticker_sentiment_label) }}
                            >
                              Sentiment: {article.ticker_sentiment_score > 0 ? '+' : ''}{article.ticker_sentiment_score.toFixed(2)}
                            </span>
                          </>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stock Info - Bottom */}
          <div className="mt-8 sm:mt-12 rounded-xl p-4 sm:p-6 md:p-8 border" style={{
            backgroundColor: 'rgba(30, 30, 30, 0.7)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h2 className="text-lg sm:text-xl font-semibold mb-6 sm:mb-8" style={{ color: '#F9F9F9' }}>
              Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div>
                <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Type</div>
                <div className="text-sm font-medium" style={{ color: '#F9F9F9' }}>{stock.type}</div>
              </div>
              <div>
                <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Last Updated</div>
                <div className="text-sm font-medium" style={{ color: '#F9F9F9' }}>
                  {new Date(stock.last_updated).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs mb-1" style={{ color: '#9E9E9E' }}>Added</div>
                <div className="text-sm font-medium" style={{ color: '#F9F9F9' }}>
                  {new Date(stock.created_at).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

